// src/utils/deviceId.js
// Durable, privacy-safe device/browser ID for mobile web.
// Strategy:
// 1) If a deviceId is already stored, use it (sticky).
// 2) If a Firebase app has been initialized, try Firebase Installations ID (FID): `fid:<id>`.
// 3) Otherwise, generate and store a UUID: `ls:<uuid>`.

import { getInstallations, getId as getFID } from "firebase/installations";
import { getApps } from "firebase/app";
import { useEffect, useState } from "react";

const STORAGE_KEY = "deviceId";
let cachedId = null;

/** Return stored ID synchronously ("" if none). */
export function getStoredDeviceIdSync() {
  if (cachedId) return cachedId;
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v) {
      cachedId = v;
      return v;
    }
  } catch {
    // storage may be blocked (private mode, etc.)
  }
  return "";
}

/** Resolve a durable ID and persist it if not already stored. */
export async function getDeviceId() {
  const existing = getStoredDeviceIdSync();
  if (existing) return existing;

  let id = null;

  // Try Firebase Installations ID if a Firebase app is initialized
  try {
    if (getApps().length) {
      const fid = await getFID(getInstallations());
      if (fid) id = `fid:${fid}`;
    }
  } catch {
    // ignore and fall through to local fallback
  }

  // Fallback: local UUID
  if (!id) {
    let uuid = "";
    try {
      uuid =
        (typeof crypto !== "undefined" && crypto.randomUUID)
          ? crypto.randomUUID()
          : Math.random().toString(36).slice(2) + Date.now().toString(36);
    } catch {
      uuid = String(Math.random()).slice(2);
    }
    id = `ls:${uuid}`;
  }

  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // if storage is blocked, return id (session-scoped)
  }
  cachedId = id;
  return id;
}

/** Clear stored ID (for local dev/testing). */
export function clearStoredDeviceId() {
  cachedId = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore */ }
}

/** React hook that returns the device ID (resolves asynchronously if needed). */
export function useDeviceId() {
  const [id, setId] = useState(getStoredDeviceIdSync());

  useEffect(() => {
    let alive = true;
    if (!id) {
      getDeviceId().then((resolved) => { if (alive) setId(resolved); });
    }
    return () => { alive = false; };
  }, [id]);

  return id;
}
